import React from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router';
import { useMetaSelector } from '../meta/reducer';
import { useAuthSelector } from './reducer';

interface RequireAuthProps {
  permission?: string;
  children: React.ReactNode;
}

export const RequireAuth = ({ permission, children }: RequireAuthProps) => {
  const meta = useMetaSelector();
  const auth = useAuthSelector();
  const location = useLocation();

  const isPaywalled =
    permission &&
    !meta.permissions.some(
      (passedPermission) => passedPermission === permission,
    );

  document.title = 'Birdi';

  // User is paywalled
  if (isPaywalled && auth.status === 'authenticated') {
    const url = new URL(window.location.href);
    url.searchParams.append('redirect', location.pathname);
    return <Navigate to={`/sign-in${url.search}`} replace />;
  }

  // User has successfully authenticated or is awaiting authentication (i.e. preloading)
  if (auth.status === 'authenticated' || auth.status === 'authenticating') {
    // Check if onboarding is incomplete and user is trying to access a protected route
    // Exclude onboarding routes from this check to avoid redirect loops
    // Only check if meta is loaded to avoid false positives
    const isOnboardingRoute = /^\/onboarding(\/.*)?$/.test(location.pathname);
    // Also check for new onboarding routes (organisation-setup, select-plan, invite-your-team)
    const isNewOnboardingRoute = [
      '/onboarding',
      '/onboarding/select-plan',
      '/onboarding/invite-your-team',
      '/onboarding/organisation-setup',
    ].includes(location.pathname);

    // If onboarding is incomplete, redirect TO /onboarding (unless already there)
    if (
      meta.loaded &&
      !meta.onboardingCompleted &&
      !isOnboardingRoute &&
      !isNewOnboardingRoute
    ) {
      return <Navigate to="/onboarding" replace />;
    }

    // If onboarding IS completed, redirect AWAY from /onboarding to dashboard
    if (
      meta.loaded &&
      meta.onboardingCompleted &&
      (isOnboardingRoute || isNewOnboardingRoute)
    ) {
      return <Navigate to="/" replace />;
    }

    return <>{children}</>;
  }

  // User is unauthenticated
  const url = new URL(window.location.href);
  url.searchParams.append('redirect', location.pathname);
  return <Navigate to={`/sign-in${url.search}`} replace />;
};
